import React from 'react';
import { Box } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export interface CurveDataset {
  label: string;
  data: number[];
  borderColor?: string;
  backgroundColor?: string;
  borderWidth?: number;
  tension?: number;
  pointRadius?: number;
  pointHoverRadius?: number;
  fill?: boolean;
}

export interface GenericCurveChartProps {
  labels: string[];
  datasets: CurveDataset[];
  title?: string;
  yAxisTitle?: string;
  xAxisTitle?: string;
  maxValue?: number;
  height?: number;
  shouldUseHorizontalLabels?: boolean;
  colors?: string[];
  options?: any
}

const defaultCurveColors = [
  'rgba(75, 192, 192, 1)',
  'rgba(162, 198, 230, 1)',
  'rgba(255, 159, 64, 1)',
  'rgba(153, 102, 255, 1)',
  'rgba(255, 99, 132, 1)',
  'rgba(54, 162, 235, 1)'
];

const defaultCurveBackgroundColors = [
  'rgba(75, 192, 192, 0.2)',
  'rgba(162, 198, 230, 0.2)',
  'rgba(255, 159, 64, 0.2)',
  'rgba(153, 102, 255, 0.2)',
  'rgba(255, 99, 132, 0.2)',
  'rgba(54, 162, 235, 0.2)'
];

export const GenericCurveChart: React.FC<GenericCurveChartProps> = ({
  labels,
  datasets,
  title = 'Curva S',
  yAxisTitle = 'Valores Acumulados',
  xAxisTitle = 'Períodos',
  maxValue,
  height = 400,
  shouldUseHorizontalLabels,
  colors = defaultCurveColors,
  options: paramOptions,
}) => {
  const { chartData, calculatedMaxValue, shouldUseHorizontal } = React.useMemo(() => {
    const itemCount = labels.length;
    const shouldUseHorizontal = shouldUseHorizontalLabels ?? itemCount <= 12;

    const enhancedDatasets = datasets.map((dataset, index) => ({
      ...dataset,
      borderColor: dataset.borderColor || colors[index % colors.length],
      backgroundColor: dataset.backgroundColor || defaultCurveBackgroundColors[index % defaultCurveBackgroundColors.length],
      borderWidth: dataset.borderWidth ?? 2,
      tension: dataset.tension ?? 0.4,
      pointRadius: dataset.pointRadius ?? 4,
      pointHoverRadius: dataset.pointHoverRadius ?? 6,
      fill: dataset.fill ?? false,
    }));

    let calculatedMax = maxValue;
    if (!calculatedMax) {
      let max = 0;
      datasets.forEach(dataset => {
        dataset.data.forEach(value => {
          max = Math.max(max, value);
        });
      });
      calculatedMax = max === 0 ? 100 : Math.floor(max * 1.1);
    }

    return {
      chartData: {
        labels,
        datasets: enhancedDatasets,
      },
      calculatedMaxValue: calculatedMax,
      shouldUseHorizontal
    };
  }, [labels, datasets, maxValue, shouldUseHorizontalLabels, colors]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          boxWidth: 12,
          font: {
            size: 11
          }
        }
      },
      title: {
        display: !!title,
        // display: false,
        text: title,
        font: {
          size: 14
        }
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          title: function (context: any) {
            return context[0].label;
          },
          label: function (context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y;
            }
            return label;
          },
          ...paramOptions?.plugins?.tooltip?.callbacks,
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#333',
        bodyColor: '#666',
        borderColor: 'rgba(0, 0, 0, 0.1)',
        borderWidth: 1,
        padding: 10,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: calculatedMaxValue.toFixed(1),
        ticks: {
          font: {
            size: 10
          },
          callback: function (value: any) {
            if (value >= 1000) {
              return value.toLocaleString('es-PE');
            }
            return value;
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        title: {
          display: true,
          text: yAxisTitle,
          font: {
            size: 12
          }
        }
      },
      x: {
        ticks: {
          font: {
            size: 10
          },
          maxRotation: shouldUseHorizontal ? 0 : 45,
          minRotation: shouldUseHorizontal ? 0 : 45,
          padding: shouldUseHorizontal ? 10 : 5,
          callback: function (this: any, value: any, index: number, values: any[]) {
            const label = this.getLabelForValue(value);
            if (shouldUseHorizontal && label && label.length > 15) {
              return label.substring(0, 15) + '...';
            }
            return label;
          }
        },
        grid: {
          display: false,
        },
        title: {
          display: false,
          text: xAxisTitle,
          font: {
            size: 12
          }
        }
      },
    },
    animation: {
      duration: 1000,
      easing: 'easeOutQuart' as const
    },
    ...paramOptions
  };

  // if (labels.length === 0 || datasets.length === 0) {
  //   return (
  //     <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
  //       No hay datos para mostrar
  //     </Box>
  //   );
  // }

  return (
    <Box sx={{ height }}>
      <Line data={chartData} options={options} />
    </Box>
  );
};